import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';
import type { FiscalReceipt } from '@/lib/fiscal';
import i18n from '@/i18n';
import { dateLocale, numberLocale } from '@/lib/locale';

export type ReceiptItem = {
  name: string;
  quantity: number;
  total: number;
  mhik?: string | null;
  unit?: string | null;
};

export type ReceiptPayment = { method: string; amount: number };

export type ReceiptData = {
  total: number;
  method: string;
  items: number;
  saleId?: number | null;
  cartItems?: ReceiptItem[];
  customerName?: string | null;
  payments?: ReceiptPayment[];
  createdAt?: string | Date | null;
  isCopy?: boolean;
  merchantName?: string | null;
  merchantAddress?: string | null;
  merchantLogo?: string | null;
};

export function paymentMethodLabel(method?: string | null): string {
  switch (method) {
    case 'cash': return i18n.t('misc_common.pay_cash');
    case 'card': return i18n.t('misc_common.pay_card');
    case 'hisob_wallet': return i18n.t('misc_common.pay_wallet');
    case 'murabaha': return i18n.t('misc_common.pay_murabaha');
    default: return method || '';
  }
}

const num = (v: number) => new Intl.NumberFormat(numberLocale()).format(Math.round(v || 0));

export function printReceipt(data: ReceiptData) {
  const printWindow = window.open('', '_blank', 'width=320,height=640');
  if (!printWindow) {
    window.print();
    return;
  }

  const itemsHtml = (data.cartItems || [])
    .map(
      (i) => `<div class="row">
        <span style="flex:1">${i.name} × ${i.quantity} ${i.unit || i18n.t('misc_receipt.default_unit')}</span>
        <span>${num(i.total)}</span>
      </div>
      ${i.mhik ? `<div class="small">MHIK: ${i.mhik}</div>` : ''}`
    )
    .join('');

  const paymentsHtml = (data.payments && data.payments.length > 1)
    ? data.payments
        .map((p) => `<div class="row"><span>${paymentMethodLabel(p.method)}</span><span>${num(p.amount)}</span></div>`)
        .join('')
    : `<div class="row"><span>${i18n.t('misc_receipt.payment_method')}</span><span>${paymentMethodLabel(data.method)}</span></div>`;

  const d = data.createdAt ? new Date(data.createdAt) : new Date();
  const dateStr = d.toLocaleDateString(dateLocale()) + ' ' + d.toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit' });

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${i18n.t('misc_receipt.cheque')}</title>
      <style>
        @page { margin: 0; size: 80mm auto; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Courier New', monospace; width: 80mm; padding: 4mm; font-size: 12px; color: #000; }
        .center { text-align: center; }
        .bold { font-weight: bold; }
        .divider { border-top: 1px dashed #000; margin: 6px 0; }
        .row { display: flex; justify-content: space-between; padding: 2px 0; }
        .total-row { display: flex; justify-content: space-between; font-weight: bold; font-size: 14px; padding: 4px 0; }
        .small { font-size: 10px; color: #666; }
      </style>
    </head>
    <body>
      ${data.merchantLogo ? `<div class="center" style="margin-bottom:6px"><img src="${data.merchantLogo}" style="max-width:48px;max-height:48px;border-radius:6px;margin:0 auto;display:block" /></div>` : ''}
      <div class="center bold" style="font-size:14px;margin-bottom:4px">${data.merchantName || 'Hisob POS'}</div>
      <div class="center small">${data.merchantAddress || ''}</div>
      <div class="center small">${dateStr}</div>
      ${data.saleId ? `<div class="center small">${i18n.t('misc_receipt.cheque_no', { id: data.saleId })}</div>` : ''}
      ${data.isCopy ? `<div class="center bold" style="margin-top:4px">${i18n.t('misc_receipt.copy')}</div>` : ''}
      ${data.customerName ? `<div class="center small">${i18n.t('misc_receipt.customer', { name: data.customerName })}</div>` : ''}
      <div class="divider"></div>
      ${itemsHtml || `<div class="row"><span>${i18n.t('misc_receipt.products')}</span><span>${i18n.t('misc_receipt.pcs', { count: data.items ?? 0 })}</span></div>`}
      <div class="divider"></div>
      ${paymentsHtml}
      <div class="divider"></div>
      <div class="total-row"><span>${i18n.t('misc_receipt.total')}</span><span>${num(data.total)} ${i18n.t('misc_common.currency')}</span></div>
      <div class="divider"></div>
      <div class="center small" style="margin-top:8px">${i18n.t('misc_receipt.thanks')}</div>
      <script>window.onload=function(){window.print();window.close();}</script>
    </body>
    </html>
  `);
  printWindow.document.close();
}

/** FR-104: 80 mm fiscal receipt of a murabaha sale — fiscal sign, OFD receipt id and the QR code. */
export function printFiscalReceipt(receipt: FiscalReceipt, opts: { merchantName?: string | null; customerName?: string | null } = {}) {
  const printWindow = window.open('', '_blank', 'width=320,height=640');
  if (!printWindow) {
    window.print();
    return;
  }

  const som = (tiyin: number) => num(tiyin / 100);
  const itemsHtml = receipt.items
    .map((i) => `<div class="row"><span style="flex:1">${esc(i.name)} × ${i.quantity}</span><span>${som(i.total)}</span></div>${i.mxik ? `<div class="small">MXIK: ${esc(i.mxik)}</div>` : ''}`)
    .join('');
  const qr = receipt.qr_url ? renderToStaticMarkup(createElement(QRCodeSVG, { value: receipt.qr_url, size: 140 })) : '';
  const d = new Date(receipt.accepted_at ?? receipt.created_at ?? Date.now());
  const dateStr = d.toLocaleDateString(dateLocale()) + ' ' + d.toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit' });

  printWindow.document.write(`<!DOCTYPE html><html><head><title>${i18n.t('misc_receipt.fiscal_cheque')}</title><style>
    @page { margin: 0; size: 80mm auto; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Courier New', monospace; width: 80mm; padding: 4mm; font-size: 12px; color: #000; }
    .center { text-align: center; } .bold { font-weight: bold; }
    .divider { border-top: 1px dashed #000; margin: 6px 0; }
    .row { display: flex; justify-content: space-between; padding: 2px 0; }
    .total-row { display: flex; justify-content: space-between; font-weight: bold; font-size: 14px; padding: 4px 0; }
    .small { font-size: 10px; color: #666; } .qr { display: flex; justify-content: center; margin: 8px 0; }
  </style></head><body>
    <div class="center bold" style="font-size:14px">${esc(opts.merchantName || 'Hisob POS')}</div>
    <div class="center small">Hisob MMT${receipt.receipt_type === 'refund' ? i18n.t('misc_receipt.refund_suffix') : ''}</div>
    <div class="center small">${dateStr}</div>
    ${receipt.order_number ? `<div class="center small">${esc(i18n.t('misc_receipt.order', { no: receipt.order_number }))}</div>` : ''}
    ${opts.customerName ? `<div class="center small">${esc(i18n.t('misc_receipt.customer', { name: opts.customerName }))}</div>` : ''}
    <div class="divider"></div>${itemsHtml}<div class="divider"></div>
    <div class="row"><span>${i18n.t('misc_common.pay_murabaha')}</span><span>${som(receipt.amount_tiyin)}</span></div>
    <div class="row"><span>${i18n.t('misc_receipt.vat', { percent: receipt.vat_percent })}</span><span>${som(receipt.vat_tiyin)}</span></div>
    <div class="total-row"><span>${i18n.t('misc_receipt.total')}</span><span>${som(receipt.amount_tiyin)} ${i18n.t('misc_common.currency')}</span></div>
    <div class="divider"></div>
    <div class="row"><span>${i18n.t('misc_receipt.fiscal_sign')}</span><span>${esc(receipt.fiscal_sign ?? '—')}</span></div>
    <div class="row"><span>${i18n.t('misc_receipt.cheque_id')}</span><span>${esc(receipt.receipt_id ?? '—')}</span></div>
    ${receipt.terminal_id ? `<div class="row"><span>${i18n.t('misc_receipt.terminal')}</span><span>${esc(receipt.terminal_id)}</span></div>` : ''}
    <div class="qr">${qr}</div>
    <div class="center small">${i18n.t('misc_receipt.thanks')}</div>
    <script>window.onload=function(){window.print();window.close();}</script>
  </body></html>`);
  printWindow.document.close();
}

const esc = (v: string) => v.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
